import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { ModalForm } from '@/components/ui/modal-form';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useVendors, useCreateVendor, useUpdateVendor, useDeleteVendor } from '@/hooks/useVendors';
import { useToast } from '@/hooks/useToast';
import type { Vendor } from '@/lib/schemas';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Plus,
  Building2,
  Mail,
  Phone,
  Search,
  AlertTriangle,
} from 'lucide-react';

export default function VendorsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [deletingVendor, setDeletingVendor] = useState<Vendor | null>(null);

  const { data: vendorsResponse = { data: [], totalCount: 0 }, isLoading, error } = useVendors({
    page,
    limit: pageSize,
  });
  const createMutation = useCreateVendor();
  const updateMutation = useUpdateVendor();
  const deleteMutation = useDeleteVendor();
  const toast = useToast();

  const vendors = vendorsResponse.data;
  const totalCount = vendorsResponse.totalCount;

  const filteredVendors = useMemo(() => {
    return vendors.filter((v) =>
      v.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [vendors, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, pageSize]);

  const stats = useMemo(() => {
    const active = vendors.filter((v) => v.is_active).length;
    const avgOTD = vendors.length > 0
      ? (vendors.reduce((sum, v) => sum + (v.on_time_delivery || 0), 0) / vendors.length).toFixed(1)
      : '0.0';
    const totalSpend = vendors.reduce((sum, v) => sum + (v.total_spend || 0), 0);
    return { total: vendors.length, active, avgOTD, totalSpend };
  }, [vendors]);

  const handleCreate = async (data: Partial<Vendor>) => {
    try {
      await createMutation.mutateAsync(
        data as Omit<Vendor, 'vendor_id' | 'created_at' | 'updated_at'>
      );
      setIsCreateOpen(false);
      toast.success('Vendor created', 'Vendor has been created successfully.');
    } catch (error: any) {
      toast.error('Failed to create vendor', error.message || 'An unknown error occurred');
    }
  };

  const handleUpdate = async (data: Partial<Vendor>) => {
    if (editingVendor && editingVendor.vendor_id) {
      try {
        await updateMutation.mutateAsync({
          id: editingVendor.vendor_id,
          data: data as Partial<Omit<Vendor, 'vendor_id' | 'created_at'>>,
        });
        setEditingVendor(null);
        toast.success('Vendor updated', 'Vendor has been updated successfully.');
      } catch (error: any) {
        toast.error('Failed to update vendor', error.message || 'An unknown error occurred');
      }
    }
  };

  const handleDelete = async () => {
    if (deletingVendor && deletingVendor.vendor_id) {
      try {
        await deleteMutation.mutateAsync(deletingVendor.vendor_id);
        setDeletingVendor(null);
        toast.success('Vendor deleted', 'Vendor has been deleted successfully.');
      } catch (error: any) {
        toast.error('Failed to delete vendor', error.message || 'An unknown error occurred');
      }
    }
  };

  const createFields = [
    {
      name: 'hospital_id',
      label: 'Hospital ID',
      type: 'number' as const,
      required: true,
      placeholder: 'Enter hospital ID',
    },
    {
      name: 'name',
      label: 'Vendor Name',
      type: 'text' as const,
      required: true,
      placeholder: 'Enter vendor name',
    },
    {
      name: 'code',
      label: 'Vendor Code',
      type: 'text' as const,
      required: true,
      placeholder: 'e.g., MED-001',
    },
    {
      name: 'contact_person',
      label: 'Contact Person',
      type: 'text' as const,
      placeholder: 'Enter contact person name',
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email' as const,
      placeholder: 'Enter email address',
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'text' as const,
      placeholder: 'Enter phone number',
    },
    {
      name: 'address',
      label: 'Address',
      type: 'textarea' as const,
      placeholder: 'Enter full address',
    },
    {
      name: 'city',
      label: 'City',
      type: 'text' as const,
      placeholder: 'Enter city',
    },
    {
      name: 'state',
      label: 'State/Province',
      type: 'text' as const,
      placeholder: 'Enter state or province',
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text' as const,
      placeholder: 'Enter country',
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text' as const,
      placeholder: 'Enter postal code',
    },
    {
      name: 'tax_id',
      label: 'Tax ID',
      type: 'text' as const,
      placeholder: 'Enter tax identification number',
    },
    {
      name: 'payment_terms',
      label: 'Payment Terms',
      type: 'text' as const,
      placeholder: 'e.g., Net 30, Net 15',
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'checkbox' as const,
      helperText: 'Whether this vendor is active',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">
            Vendors
          </h1>
          <p className="text-text-secondary mt-1">
            Manage supplier relationships and performance
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Vendor
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-red-900">Error loading vendors</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <Card>
              <CardHeader className="pb-2">
                <div className="h-4 w-24 bg-border rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-12 bg-border rounded animate-pulse" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <div className="h-4 w-16 bg-border rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-8 bg-border rounded animate-pulse" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <div className="h-4 w-32 bg-border rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-12 bg-border rounded animate-pulse" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <div className="h-4 w-20 bg-border rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-24 bg-border rounded animate-pulse" />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Vendors</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{stats.total}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Active</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Avg. On-Time Delivery</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{stats.avgOTD}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Spend</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalSpend)}</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search vendors by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                aria-label="Search vendors"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={pageSize} columns={6} />
      ) : vendors.length > 0 ? (
        <>
          <DataTable
            columns={[
              {
                key: 'name',
                header: 'Vendor',
                render: (vendor: Vendor) => (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{vendor.name}</p>
                      <p className="text-xs text-text-muted">{vendor.code}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: 'contact_email',
                header: 'Contact',
                render: (vendor: Vendor) => (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3 w-3 text-text-muted" />
                      <span className="text-text-secondary">{vendor.contact_email || 'No email'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-3 w-3 text-text-muted" />
                      <span className="text-text-secondary">{vendor.contact_phone || 'No phone'}</span>
                    </div>
                  </div>
                ),
              },
              {
                key: 'payment_terms',
                header: 'Payment Terms',
                render: (vendor: Vendor) => (
                  <span className="text-text-secondary">{vendor.payment_terms || 'Not set'}</span>
                ),
              },
              {
                key: 'on_time_delivery',
                header: 'OTD %',
                render: (vendor: Vendor) => (
                  <span className={cn(
                    'font-semibold',
                    (vendor.on_time_delivery || 0) >= 95 ? 'text-green-600' :
                    (vendor.on_time_delivery || 0) >= 90 ? 'text-yellow-600' : 'text-red-600'
                  )}>
                    {vendor.on_time_delivery?.toFixed(1) || 'N/A'}%
                  </span>
                ),
              },
              {
                key: 'total_spend',
                header: 'Total Spend',
                render: (vendor: Vendor) => (
                  <span className="font-mono text-text-primary">
                    {formatCurrency(vendor.total_spend || 0)}
                  </span>
                ),
              },
              {
                key: 'is_active',
                header: 'Status',
                render: (vendor: Vendor) => (
                  <Badge variant={vendor.is_active ? 'success' : 'error'}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                ),
              },
            ]}
            data={vendors}
            loading={false}
            onEdit={setEditingVendor}
            onDelete={setDeletingVendor}
            getItemId={(vendor) => vendor.vendor_id!}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />

          <DataTablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
          />
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No vendors found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Add your first vendor to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {filteredVendors.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No vendors found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Add your first vendor to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <ModalForm<Vendor>
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        title="Add New Vendor"
        description="Create a new vendor record."
        fields={createFields}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      {/* Edit Modal */}
      <ModalForm<Vendor>
        open={!!editingVendor}
        onOpenChange={(open) => !open && setEditingVendor(null)}
        title="Edit Vendor"
        description={`Editing: ${editingVendor?.name}`}
        fields={createFields}
        initialData={editingVendor || undefined}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deletingVendor}
        onOpenChange={(open) => !open && setDeletingVendor(null)}
        title="Delete Vendor"
        description={`Are you sure you want to delete "${deletingVendor?.name}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
